import { create } from 'zustand'

import { get as apiGet, post } from '@/api/request'
import type { MiningSession, MiningStats } from '@/types/api'

import { useAssetsStore } from './assetsStore'

interface MiningStore {
  currentSession: MiningSession | null
  claimableYield: number
  claimableCount: number
  stats: MiningStats | null
  isLoading: boolean
  error: string | null
  _initialized: boolean
  fetch: () => Promise<void>
  startMining: () => Promise<{ success: boolean; error?: string }>
  claimRewards: () => Promise<{
    success: boolean
    claimedAmount?: number
    newBalance?: number
    error?: string
  }>
  reset: () => void
}

export const useMiningStore = create<MiningStore>((set, get) => ({
  currentSession: null,
  claimableYield: 0,
  claimableCount: 0,
  stats: null,
  isLoading: false,
  error: null,
  _initialized: false,

  async fetch() {
    if (!get()._initialized) {
      set({ isLoading: true, error: null })
    }
    try {
      const res = await apiGet<{
        currentSession: MiningSession | null
        claimableYield: number
        claimableCount: number
        stats: MiningStats
      }>('/mining/status')
      if (res.data?.data) {
        const d = res.data.data
        set({
          currentSession: d.currentSession,
          claimableYield: d.claimableYield ?? 0,
          claimableCount: d.claimableCount ?? 0,
          stats: d.stats,
          error: null,
        })
      }
    } catch (err) {
      if (!get()._initialized) {
        set({ error: err instanceof Error ? err.message : 'Failed' })
      }
    } finally {
      set({ isLoading: false, _initialized: true })
    }
  },

  async startMining() {
    try {
      await post('/mining/start')
      await get().fetch()
      return { success: true }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed' }
    }
  },

  async claimRewards() {
    try {
      const res = await post<{ claimedAmount: number; newBalance: number }>('/mining/claim')
      if (res.data?.data) {
        await get().fetch()
        // 领取成功后同步刷新资产余额与交易记录，保证 Header / Assets 页面展示最新数据
        const assets = useAssetsStore.getState()
        void assets.fetchBalance()
        void assets.fetchTransactions()
        return {
          success: true,
          claimedAmount: res.data.data.claimedAmount,
          newBalance: res.data.data.newBalance,
        }
      }
      return { success: false, error: 'Failed' }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed' }
    }
  },

  reset() {
    set({
      currentSession: null,
      claimableYield: 0,
      claimableCount: 0,
      stats: null,
      isLoading: false,
      error: null,
      _initialized: false,
    })
  },
}))
